import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import secureStorage from '../services/secureStorage';
import encryptedSessionStorage from '../services/encryptedSessionStorage';

export const SHOP_STORAGE_KEY = 'selected_shop';

export interface SelectedShopState {
  id: string;
  name: string;
  show?: boolean;
}

interface ShopState {
  selectedShop: SelectedShopState;
}

// Synchronous read for Redux initialState — reads the raw value from sessionStorage.
// Legacy plaintext values are parsed as-is; encrypted ciphertext will throw (caught → default).
// The AppNavigator bootstrap overwrites this with the fully-decrypted secureStorage value.
export function getPersistedShop(): SelectedShopState {
  try {
    const stored = encryptedSessionStorage.getItemSync(SHOP_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as SelectedShopState;
    }
  } catch {
    // ignore parse errors / encrypted ciphertext
  }
  return { id: '', name: '', show: true };
}

const initialState: ShopState = {
  selectedShop: getPersistedShop(),
};

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    setSelectedShop(state, action: PayloadAction<SelectedShopState>) {
      state.selectedShop = action.payload;
      // Persist to secureStorage on all platforms (encrypted)
      if (action.payload.id) {
        secureStorage.setItem(SHOP_STORAGE_KEY, JSON.stringify(action.payload)).catch(() => {});
      } else {
        secureStorage.removeItem(SHOP_STORAGE_KEY).catch(() => {});
      }
    },
  },
});

export const { setSelectedShop } = shopSlice.actions;
export default shopSlice.reducer;
